/**
 * The board header (spec M1-B1 §7.3, §7.4).
 *
 * Band y 96→139, controls 32 px high `[uij:library/board-01-header.json]`. Left to right:
 * the title (`EditableHeading`, title family 24/500, box 32, padding `1px 4px`, radius 4,
 * hover `rgba(103,104,121,0.1)`), the `⌄` that opens the info popover, the kind badge, then
 * the action bar — **Integrate** · **Automate / N** · the owner avatar group · **Invite / N**
 * with a copy-link button · `···`.
 *
 * What monday has and this product does not (AI suggestions, Agents, Convert board to project,
 * Give feedback, Power-Ups) is **absent**, not disabled — §3.2 — and the order of the rest is
 * preserved exactly.
 */
import {
  Avatar,
  Icon,
  IconButton,
  Tooltip,
  icons,
  useConfirm,
  useToast,
} from '@elizmonday/ui';
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import { initialsOf } from '../board-view/columns/primitives';
import { usePhone } from '../board-view/table/phone';
import { requestBoardPrint } from '../board-view/print-request';
import type { MenuEntry } from '../board-view/table/menus/Menu';
import { boardUrl } from '../shell/urls';
import { AutomationsCenter } from '../automations/AutomationsCenter';
import { useAutomationsBadge, useAutomationsSource, useInvalidateAutomationsBadge } from '../automations/hooks';
import '../automations/automations.css';
import { BoardInfoPopover } from './BoardInfoPopover';
import { BoardMenu } from './BoardMenu';
import { BoardSubscribersDialog } from './BoardSubscribersDialog';
import { BoardArchiveDialog } from './BoardArchiveDialog';
import { DuplicateBoardDialog } from './DuplicateBoardDialog';
import { ItemTerminologyDialog } from './ItemTerminologyDialog';
import { MoveBoardDialog } from './MoveBoardDialog';
import { ExportDialog } from '../export';
import { SaveAsTemplateDialog } from '../templates';
import { BoardPermissionsDialog } from '../admin/sharing';
import { createAdminSource, type AdminTrpc } from '../admin/trpc-source';
import '../admin/admin.css';
import { BoardNotificationsDialog } from './BoardNotificationsDialog';
import { useTRPCClient } from '../../lib/trpc';
import './boards.css';
import {
  BOARD_KIND_LABEL,
  useBoard,
  useBoardMembers,
  useBoardMutations,
  type BoardKind,
} from './hooks';

export interface BoardHeaderProps {
  boardId: string;
  viewId: string | null;
  /** §7.3: with the sidebar pane expanded the action buttons are icon-only (H8). */
  labelled?: boolean;
}

/** B9 adds `export` and `template`; the two slots the menu had reserved since M1-B1 §3.3. */
export type BoardDialog =
  | 'subscribers'
  | 'duplicate'
  | 'terminology'
  | 'move'
  | 'export'
  | 'template'
  /** B10: the two entries monday's `···` opens directly, which ours rendered disabled. */
  | 'permissions'
  | 'notifications'
  /** 6i (q452): the board's own archived and deleted items, now that `items.listRemoved` exists. */
  | 'archive-trash'
  | null;

export function BoardHeader({ boardId, viewId, labelled = false }: BoardHeaderProps) {
  const board = useBoard(boardId);
  /**
   * P-042 §1. On a real board the band's content measured **639 px inside a 326 px band**
   * (iPhone 14) and an ancestor clips it, so `Automate`, the owners, `Invite`, the copy-link
   * button *and `···` itself* took no tap at all. Below `breakpoints.phone` the widget row is
   * therefore not rendered — it moves into the `···` menu as `leading` entries — and only the
   * title, the info chevron, the kind badge, a two-avatar owner stack and `···` stay in the row.
   * `usePhone()` is `false` at every other width, so nothing about the captured desktop band
   * changes (M0-F9 D-0).
   */
  const phone = usePhone();
  const members = useBoardMembers(boardId);
  const mutations = useBoardMutations();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const [editingTitle, setEditingTitle] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [menuAt, setMenuAt] = useState<{ x: number; y: number } | null>(null);
  const [dialog, setDialog] = useState<BoardDialog>(null);
  /** M6-A2: **Integrate** and **Automate** open the same modal on its two tabs (H5). */
  const [centerTab, setCenterTab] = useState<'create' | 'manage' | null>(null);
  const automationsSource = useAutomationsSource();
  const automationsBadge = useAutomationsBadge(boardId);
  const invalidateBadge = useInvalidateAutomationsBadge();
  const titleRef = useRef<HTMLInputElement>(null);
  /** B10: the board menu's `Permissions` entry opens M8.P1's dialog, which already existed. */
  const trpcClient = useTRPCClient();
  const adminSource = useMemo(
    () => createAdminSource(trpcClient as unknown as AdminTrpc),
    [trpcClient],
  );

  useEffect(() => {
    if (editingTitle) titleRef.current?.select();
  }, [editingTitle]);

  const name = board.data?.name ?? '';
  const kind = (board.data?.kind ?? 'public') as BoardKind;
  const state = board.data?.state ?? 'active';

  /** H2: Enter commits, Escape reverts, empty is rejected, > 255 chars is rejected. */
  const commitTitle = (next: string) => {
    setEditingTitle(false);
    const trimmed = next.trim();
    if (trimmed === '' || trimmed === name) return;
    if (trimmed.length > 255) {
      toast('A board name is at most 255 characters', { kind: 'negative' });
      return;
    }
    mutations.update.mutate({ id: boardId, attribute: 'name', value: trimmed });
  };

  const onTitleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      commitTitle(event.currentTarget.value);
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      setEditingTitle(false);
    }
  };

  const copyLink = async () => {
    const url = `${window.location.origin}${boardUrl(boardId)}`;
    try {
      await navigator.clipboard.writeText(url);
      toast('Board link copied', { kind: 'positive' });
    } catch {
      toast('The link could not be copied', { kind: 'negative' });
    }
  };

  const archive = async () => {
    const ok = await confirm({
      title: `Archive ${name}?`,
      body: 'It leaves the sidebar and stays readable at its URL. You can restore it any time.',
      confirmLabel: 'Archive',
    });
    if (!ok) return;
    await mutations.archive.mutateAsync(boardId);
    // B10: the 10 s undo toast, which is what makes "archive" a safe click.
    toast(`${name} archived`, { kind: 'positive', action: { label: 'Undo', onClick: () => void restore() } });
  };

  const restore = async () => {
    await mutations.restore.mutateAsync(boardId);
    toast(`${name} restored`, { kind: 'positive' });
  };

  /** B11: a board with items asks the person to type its name before it is deleted. */
  const remove = async () => {
    const ok = await confirm({
      title: `Delete ${name}?`,
      body: 'The board moves to the trash. Anyone who could delete it can restore it for the next 30 days.',
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    await mutations.remove.mutateAsync(boardId);
    toast(`${name} moved to the trash`, {
      kind: 'positive',
      action: { label: 'Undo', onClick: () => void undoDelete() },
    });
    navigate('/');
  };

  const undoDelete = async () => {
    await mutations.restoreFromTrash.mutateAsync(boardId);
    toast(`${name} restored`, { kind: 'positive' });
    navigate(boardUrl(boardId));
  };

  const owners = (members.data ?? []).filter((member) => member.role === 'owner');
  /** Three avatars are 84 px of a 326 px band; two plus `+N` is 60 and still says "who". */
  const shownOwners = owners.slice(0, phone ? 2 : 3);

  /**
   * The widget row, as menu entries, for the phone `···`. Same handlers as the buttons above —
   * one action, two presentations — so nothing became unreachable and no behaviour forked.
   */
  const phoneMenuEntries: MenuEntry[] = phone
    ? [
        {
          id: 'phone-automate',
          label: `Automate / ${String(automationsBadge.data ?? 0)}`,
          'data-testid': 'board-menu-automate',
          onSelect: () => {
            setMenuAt(null);
            setCenterTab(automationsBadge.data === 0 ? 'create' : 'manage');
          },
        },
        {
          id: 'phone-integrate',
          label: 'Integrate',
          'data-testid': 'board-menu-integrate',
          onSelect: () => {
            setMenuAt(null);
            setCenterTab('create');
          },
        },
        {
          id: 'phone-invite',
          label: `Invite / ${String(members.data?.length ?? 0)}`,
          'data-testid': 'board-menu-invite',
          onSelect: () => {
            setMenuAt(null);
            setDialog('subscribers');
          },
        },
        {
          id: 'phone-copy-link',
          label: 'Copy the board link',
          'data-testid': 'board-menu-copy-link',
          onSelect: () => {
            setMenuAt(null);
            void copyLink();
          },
        },
      ]
    : [];

  return (
    <header className="ez-board-header" data-testid="board-header">
      {state !== 'active' ? (
        <div className="ez-board-header__banner" role="status" data-testid="board-state-banner">
          <span>{state === 'archived' ? 'This board is archived' : 'This board is in the trash'}</span>
          <button
            type="button"
            onClick={() => void (state === 'archived' ? restore() : undoDelete())}
            data-testid="board-state-restore"
          >
            Restore
          </button>
        </div>
      ) : null}

      <div className="ez-board-header__band">
        {editingTitle ? (
          <input
            ref={titleRef}
            className="ez-board-header__title-input"
            defaultValue={name}
            aria-label="Board name"
            maxLength={255}
            data-testid="board-title-input"
            onKeyDown={onTitleKeyDown}
            onBlur={(event) => commitTitle(event.target.value)}
          />
        ) : (
          <h1
            className="ez-board-header__title"
            data-testid="board-title"
            /* P-042 §1: the row is one line with an ellipsis on a phone, so the full name has to
               be reachable — the native tooltip on a pointer, and the full-width inline editor a
               tap away on a finger. */
            title={name}
            tabIndex={0}
            role="button"
            onClick={() => setEditingTitle(true)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') setEditingTitle(true);
            }}
          >
            {name}
          </h1>
        )}

        <IconButton
          icon={icons.chevronDown}
          ariaLabel="Board information"
          size="xs"
          kind="tertiary"
          active={infoOpen}
          onClick={() => setInfoOpen((open) => !open)}
          data-testid="board-info-toggle"
        />

        <span className="ez-board-header__kind" data-testid="board-kind-badge">
          {BOARD_KIND_LABEL[kind]}
        </span>

        <span className="ez-board-header__spacer" />

        {/* Integrate and Automate open the **same** modal, on its Create and Manage tabs (H5). */}
        {phone ? null : (
          <>
        <button
          type="button"
          className="ez-board-header__action"
          data-testid="board-integrate"
          onClick={() => setCenterTab('create')}
        >
          <Icon icon={icons.apps} size={16} ignoreFocusStyle />
          {labelled ? 'Integrate' : null}
        </button>
        <button
          type="button"
          className="ez-board-header__action"
          data-testid="board-automate"
          onClick={() => setCenterTab(automationsBadge.data === 0 ? 'create' : 'manage')}
        >
          <Icon icon={icons.automation} size={16} ignoreFocusStyle />
          {labelled ? 'Automate' : null}
          <span className="ez-board-header__badge">{`/ ${String(automationsBadge.data ?? 0)}`}</span>
        </button>
          </>
        )}

        <button
          type="button"
          className="ez-board-header__owners"
          aria-label="Board owners"
          data-testid="board-owners"
          onClick={() => setDialog('subscribers')}
        >
          {shownOwners.map((owner) => (
            <Avatar key={owner.id} text={initialsOf(owner.name)} type="text" size="small" aria-label={owner.name} />
          ))}
          {owners.length > shownOwners.length ? (
            <span className="ez-board-header__badge">{`+${String(owners.length - shownOwners.length)}`}</span>
          ) : null}
        </button>

        {phone ? null : (
          <>
        {/* P-043: `Invite / N` opens the board people dialog — monday's *Invite to this
            board* — which is where inviting an address to the account now lives too. The
            avatar cluster to its left opens the same dialog. */}
        <button
          type="button"
          className="ez-board-header__action"
          data-testid="board-invite"
          onClick={() => setDialog('subscribers')}
        >
          <Icon icon={icons.invite} size={16} ignoreFocusStyle />
          {labelled ? 'Invite' : null}
          <span className="ez-board-header__badge">{`/ ${String(members.data?.length ?? 0)}`}</span>
        </button>
        <Tooltip content="Copy the board link">
          <IconButton
            icon={icons.link}
            ariaLabel="Copy the board link"
            size="xs"
            kind="tertiary"
            onClick={() => void copyLink()}
            data-testid="board-copy-link"
          />
        </Tooltip>
          </>
        )}

        <IconButton
          icon={icons.moreActions}
          ariaLabel="Board options"
          size="xs"
          kind="tertiary"
          onClick={(event) => {
            const box = (event.currentTarget as HTMLElement).getBoundingClientRect();
            setMenuAt({ x: box.right - 264, y: box.bottom + 4 });
          }}
          data-testid="board-menu-toggle"
        />
      </div>

      {infoOpen ? (
        <BoardInfoPopover
          boardId={boardId}
          onClose={() => setInfoOpen(false)}
          /* P-041: the popover's rows are live — Owners opens the subscribers dialog,
             Permissions the board permissions dialog and Notifications the board mute
             dialog, which is what each row opens on monday (`[c:board-info-notifications]`). */
          onOpen={(target) => {
            setInfoOpen(false);
            setDialog(target);
          }}
        />
      ) : null}

      {menuAt ? (
        <BoardMenu
          boardId={boardId}
          viewId={viewId}
          x={menuAt.x}
          y={menuAt.y}
          leading={phoneMenuEntries}
          onClose={() => setMenuAt(null)}
          onArchive={() => void archive()}
          onDelete={() => void remove()}
          onOpenDialog={setDialog}
          onPrint={requestBoardPrint}
        />
      ) : null}

      {dialog === 'subscribers' ? (
        <BoardSubscribersDialog boardId={boardId} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'duplicate' ? (
        <DuplicateBoardDialog boardId={boardId} onClose={() => setDialog(null)} />
      ) : null}
      {dialog === 'move' ? <MoveBoardDialog boardId={boardId} onClose={() => setDialog(null)} /> : null}
      {dialog === 'terminology' ? (
        <ItemTerminologyDialog boardId={boardId} onClose={() => setDialog(null)} />
      ) : null}
      {/* B9: the two entries `More actions` had been holding open. */}
      {dialog === 'export' ? <ExportDialog boardId={boardId} onClose={() => setDialog(null)} /> : null}
      {dialog === 'template' ? (
        <SaveAsTemplateDialog boardId={boardId} boardName={name} onClose={() => setDialog(null)} />
      ) : null}
      {/* B10: the two entries the `···` menu used to render disabled. */}
      {dialog === 'permissions' ? (
        <div className="ez-dialog-backdrop" role="presentation" onMouseDown={() => setDialog(null)}>
          <div onMouseDown={(event) => event.stopPropagation()}>
            <BoardPermissionsDialog
              source={adminSource}
              boardId={boardId}
              onClose={() => setDialog(null)}
              onShare={() => setDialog('subscribers')}
            />
          </div>
        </div>
      ) : null}
      {dialog === 'notifications' ? (
        <BoardNotificationsDialog boardId={boardId} boardName={name} onClose={() => setDialog(null)} />
      ) : null}
      {/* 6i: the last entry the `···` menu rendered disabled. */}
      {dialog === 'archive-trash' ? (
        <BoardArchiveDialog boardId={boardId} boardName={name} onClose={() => setDialog(null)} />
      ) : null}

      {centerTab === null ? null : (
        <AutomationsCenter
          source={automationsSource}
          boardId={boardId}
          boardName={name}
          accountName="Eli Z Group"
          initialTab={centerTab}
          onClose={() => {
            setCenterTab(null);
            invalidateBadge(boardId);
          }}
        />
      )}
    </header>
  );
}
